import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { delftBlue, flame } from "@/theme/colors";
import { getSwitchStyles } from "@/utils/switchStyles";

const ContactButtonConfig = ({
  title,
  buttonLabel,
  description,
  buttonName,
  contactNumber,
  disabled = false,
  isEmergency = false,
  onToggle,
  onButtonNameChange,
  onContactNumberChange,
  onCancel,
  onSave,
  hideButtons = false,
  hideToggle = false,
  isSaving = false,
  contactNumberError = null,
  contactNumberPlaceholder,
  successMessage = null,
  isSuperAdmin = false,
}) => {
  // const theme = useTheme();
  const isActive = !disabled;
  const [contactNumberTouched, setContactNumberTouched] = useState(false);

  // For admin roles (not super admin), allow editing even when toggle is disabled
  // For super admin, keep current behavior (fields disabled when toggle is off)
  const shouldDisableFields = isSuperAdmin ? disabled : false;

  // Same Australian formats for every field (emergency + contact buttons); validation matches.
  const numberPlaceholder =
    contactNumberPlaceholder ||
    "e.g. +1 202 555 0123, +44 20 7946 0958, 112";

  return (
    <Box
      sx={{
        flex: "1 1 0",
        minWidth: 0,
        width: "100%",
        padding: { xs: 1.5, sm: 2 },
        background: "white",
        borderRadius: 2,
        outline: isEmergency
          ? `1px ${flame[500]} solid`
          : `1px ${delftBlue[100]} solid`,
        outlineOffset: "-1px",
        display: "flex",
        flexDirection: "column",
        gap: { xs: 2, sm: 2.5, md: 3 },
      }}
    >
      {/* Success Message */}
      {successMessage && (
        <Alert
          severity="success"
          sx={{
            alignSelf: "stretch",
            borderRadius: 2,
          }}
        >
          {successMessage}
        </Alert>
      )}

      {/* Toggle Switch */}
      {!hideToggle && (
        <Box
          sx={{
            alignSelf: "stretch",
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
            gap: { xs: 1.5, sm: 2 },
            flexWrap: { xs: "wrap", sm: "nowrap" },
          }}
        >
          <Box
            sx={{
              flex: "1 1 0",
              minWidth: { xs: "100%", sm: 160 },
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "center",
            }}
          >
            <Typography
              sx={{
                flex: "1 1 0",
                color: delftBlue[500],
                fontSize: { xs: 14, sm: 15, md: 16 },
                fontFamily: "Montserrat, sans-serif",
                fontWeight: 600,
                lineHeight: 1.5,
              }}
            >
              {title}
            </Typography>
          </Box>
          <Switch
            checked={isActive}
            onChange={onToggle}
            disabled={(isEmergency && !onToggle) || isSaving}
            sx={getSwitchStyles(false)}
          />
        </Box>
      )}

      {/* Form Fields */}
      <Stack spacing={{ xs: 1.5, sm: 2 }} sx={{ alignSelf: "stretch" }}>
        {/* Button Name Field */}
        <Box
          sx={{
            alignSelf: "stretch",
            display: "flex",
            flexDirection: "column",
            gap: { xs: 1.5, sm: 2 },
          }}
        >
          <Box
            sx={{
              alignSelf: "stretch",
              minWidth: { xs: "100%", sm: 160 },
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            <Box
              sx={{
                alignSelf: "stretch",
                paddingBottom: { xs: 0.5, sm: 1 },
                display: "flex",
                flexDirection: "column",
                gap: { xs: 0.5, sm: 1 },
              }}
            >
              <Typography
                sx={{
                  alignSelf: "stretch",
                  color: delftBlue[500],
                  fontSize: { xs: 14, sm: 15, md: 16 },
                  fontFamily: "Montserrat, sans-serif",
                  fontWeight: 600,
                  lineHeight: 1.5,
                }}
              >
                {buttonLabel ||
                  (isEmergency ? "Emergency Button Name" : "Button Name")}
              </Typography>
              <Typography
                sx={{
                  alignSelf: "stretch",
                  color: delftBlue[500],
                  fontSize: { xs: 12, sm: 13 },
                  fontFamily: "Montserrat, sans-serif",
                  fontWeight: 400,
                  wordWrap: "break-word",
                  overflowWrap: "break-word",
                }}
              >
                {description ||
                  "This name will appear as the call button label and can be edited by the companies you add."}
              </Typography>
            </Box>
          </Box>
          <TextField
            fullWidth
            value={buttonName}
            onChange={(e) =>
              onButtonNameChange && onButtonNameChange(e.target.value)
            }
            disabled={shouldDisableFields || (isEmergency && !onButtonNameChange) || isSaving}
            placeholder="Call A"
            inputProps={{ maxLength: 50 }}
            sx={{
              height: { xs: 48, sm: 56 },
              "& .MuiOutlinedInput-root": {
                height: { xs: 48, sm: 56 },
                paddingLeft: { xs: 1.25, sm: 1.5 },
                paddingRight: { xs: 1.25, sm: 1.5 },
                background: isEmergency ? delftBlue[100] : "white",
                borderRadius: 2,
                outline: isEmergency
                  ? `1px ${delftBlue[50]} solid`
                  : `1px ${delftBlue[100]} solid`,
                outlineOffset: "-1px",
                "& fieldset": {
                  border: "none",
                },
                "& input": {
                  color: isEmergency ? delftBlue[500] : delftBlue[500],
                  fontSize: { xs: 14, sm: 15, md: 16 },
                  fontFamily: "Montserrat, sans-serif",
                  fontWeight: 400,
                  lineHeight: 1.5,
                  "&::placeholder": {
                    color: delftBlue[400],
                    opacity: 1,
                  },
                },
                "&:hover": {
                  outline: isEmergency
                    ? `1px ${delftBlue[50]} solid`
                    : `1px ${delftBlue[100]} solid`,
                },
                "&.Mui-focused": {
                  outline: isEmergency
                    ? `1px ${delftBlue[50]} solid`
                    : `1px ${delftBlue[100]} solid`,
                },
              },
            }}
          />
        </Box>

        {/* Contact Number Field */}
        <Box
          sx={{
            alignSelf: "stretch",
            display: "flex",
            flexDirection: "column",
            gap: { xs: 1.5, sm: 2 },
          }}
        >
          <Box
            sx={{
              alignSelf: "stretch",
              minWidth: { xs: "100%", sm: 160 },
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            <Box
              sx={{
                alignSelf: "stretch",
                paddingBottom: { xs: 0.5, sm: 1 },
                display: "flex",
                flexDirection: "column",
                gap: { xs: 0.5, sm: 1 },
              }}
            >
              <Typography
                sx={{
                  alignSelf: "stretch",
                  color: delftBlue[500],
                  fontSize: { xs: 14, sm: 15, md: 16 },
                  fontFamily: "Montserrat, sans-serif",
                  fontWeight: 600,
                  lineHeight: 1.5,
                }}
              >
                Contact Number
              </Typography>
              <Typography
                sx={{
                  alignSelf: "stretch",
                  color: delftBlue[500],
                  fontSize: { xs: 12, sm: 13 },
                  fontFamily: "Montserrat, sans-serif",
                  fontWeight: 400,
                  wordWrap: "break-word",
                  overflowWrap: "break-word",
                }}
              >
                The number will be dialed as soon as the user presses the button
                associated with it.
              </Typography>
            </Box>
          </Box>
          <TextField
            fullWidth
            value={contactNumber}
            onChange={(e) => {
              if (!contactNumberTouched) {
                setContactNumberTouched(true);
              }
              onContactNumberChange && onContactNumberChange(e.target.value);
            }}
            onFocus={() => setContactNumberTouched(true)}
            disabled={shouldDisableFields || (isEmergency && !onContactNumberChange) || isSaving}
            placeholder={numberPlaceholder}
            error={!!contactNumberError}
            helperText={contactNumberError || ""}
            FormHelperTextProps={{
              sx: {
                marginLeft: 0,
                marginTop: 0.5,
                fontSize: { xs: 11, sm: 12 },
                fontFamily: "Montserrat, sans-serif",
                color: contactNumberError ? "error.main" : delftBlue[400],
                minHeight: contactNumberError ? "auto" : 0,
              },
            }}
            sx={{
              "& .MuiFormHelperText-root": {
                margin: 0,
                marginTop: 0.5,
              },
              "& .MuiOutlinedInput-root": {
                height: { xs: 48, sm: 56 },
                paddingLeft: { xs: 1.25, sm: 1.5 },
                paddingRight: { xs: 1.25, sm: 1.5 },
                background: isEmergency ? delftBlue[100] : "white",
                borderRadius: 2,
                outline: contactNumberError
                  ? `1px solid`
                  : isEmergency
                    ? `1px ${delftBlue[50]} solid`
                    : `1px ${delftBlue[100]} solid`,
                ...(contactNumberError && { outlineColor: "error.main" }),
                outlineOffset: "-1px",
                "& fieldset": {
                  border: "none",
                },
                "& input": {
                  color: isEmergency ? delftBlue[500] : delftBlue[500],
                  fontSize: { xs: 14, sm: 15, md: 16 },
                  fontFamily: "Montserrat, sans-serif",
                  fontWeight: 400,
                  lineHeight: 1.5,
                  "&::placeholder": {
                    color: delftBlue[400],
                    opacity: 1,
                  },
                },
                "&:hover": {
                  outline: contactNumberError
                    ? `1px solid`
                    : isEmergency
                      ? `1px ${delftBlue[50]} solid`
                      : `1px ${delftBlue[100]} solid`,
                  ...(contactNumberError && { outlineColor: "error.main" }),
                },
                "&.Mui-focused": {
                  outline: contactNumberError
                    ? `1px solid`
                    : isEmergency
                      ? `1px ${delftBlue[50]} solid`
                      : `1px ${delftBlue[100]} solid`,
                  ...(contactNumberError && { outlineColor: "error.main" }),
                },
                "&.Mui-error": {
                  outline: `1px solid`,
                  outlineColor: "error.main",
                },
              },
            }}
          />
        </Box>
      </Stack>

      {/* Action Buttons or Note */}
      {hideButtons ? (
        <Box
          sx={{
            alignSelf: "stretch",
            padding: { xs: 1.5, sm: 2 },
            borderRadius: 2,
            background: delftBlue[50],
            border: `1px ${delftBlue[100]} solid`,
          }}
        >
          <Typography
            sx={{
              color: delftBlue[500],
              fontSize: { xs: 13, sm: 14, md: 15 },
              fontFamily: "Montserrat, sans-serif",
              fontWeight: 400,
              lineHeight: 1.5,
            }}
          >
            Note* : Contact your Super administrator for any query
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            alignSelf: "stretch",
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "flex-start",
            gap: { xs: 1, sm: 1.5 },
            flexDirection: { xs: "column", sm: "row" },
          }}
        >
          <Button
            onClick={onCancel}
            disabled={!onCancel || isSaving}
            sx={{
              height: { xs: 44, sm: 48 },
              minWidth: { xs: "100%", sm: 79 },
              width: { xs: "100%", sm: "auto" },
              paddingLeft: { xs: 1.5, sm: 2 },
              paddingRight: { xs: 1.5, sm: 2 },
              paddingTop: { xs: 0.75, sm: 1 },
              paddingBottom: { xs: 0.75, sm: 1 },
              background: delftBlue[900],
              borderRadius: 2,
              justifyContent: "center",
              alignItems: "center",
              gap: 1.5,
              textTransform: "none",
              "&:hover": {
                background: delftBlue[800],
              },
            }}
          >
            <Typography
              sx={{
                color: "white",
                fontSize: { xs: 14, sm: 15, md: 16 },
                fontFamily: "Montserrat, sans-serif",
                fontWeight: 600,
              }}
            >
              Cancel
            </Typography>
          </Button>
          <Button
            onClick={onSave}
            disabled={!onSave || isSaving || (isSuperAdmin && disabled)}
            sx={{
              flex: { xs: "0 0 auto", sm: "1 1 0" },
              width: { xs: "100%", sm: "auto" },
              alignSelf: "stretch",
              paddingLeft: { xs: 1.5, sm: 2 },
              paddingRight: { xs: 1.5, sm: 2 },
              paddingTop: { xs: 0.75, sm: 1.5 },
              paddingBottom: { xs: 0.75, sm: 1.5 },
              background: isSaving ? flame[400] : flame[500],
              borderRadius: 2,
              justifyContent: "center",
              alignItems: "center",
              gap: 1.5,
              textTransform: "none",
              "&:hover": {
                background: isSaving ? flame[400] : flame[600],
              },
              "&:disabled": {
                background: flame[400],
                opacity: 0.7,
              },
            }}
          >
            {isSaving ? (
              <CircularProgress size={20} sx={{ color: "white" }} />
            ) : (
              <Typography
                sx={{
                  color: "white",
                  fontSize: { xs: 14, sm: 15, md: 16 },
                  fontFamily: "Montserrat, sans-serif",
                  fontWeight: 600,
                }}
              >
                Save Changes
              </Typography>
            )}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default ContactButtonConfig;
